import { AlarmClock } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { CollabExpiry } from "@/types/collab"

/**
 * OKS-483 — Expiring-soon nudge (UX A1).
 *
 * A display-only, loss-aversion banner layered on the §08 detail sheet of a
 * still-submittable collab (Open `accepted` / Drop·Promo `delivered`) when the
 * server-computed `expiry.tier === "expiring_soon"` (within 24h of the
 * effective expiry instant). The FE NEVER re-derives the window — it reads
 * `expiry.tier` + `expiry.hours_remaining` straight off the wire
 * (`Collabs::ExpiryInfo`).
 *
 * Renders nothing unless `tier === "expiring_soon"`, so callers can mount it
 * unconditionally. It changes copy/urgency ONLY — the primary Submit CTA is
 * untouched (UX A1).
 *
 * A11y: `role="status"` (non-modal urgency, NOT an alert — it must not steal
 * focus or interrupt the screen reader). The pill carries text, never colour
 * alone (UX F).
 */
export interface ExpiringSoonBannerProps {
  expiry: CollabExpiry
  className?: string
}

export function ExpiringSoonBanner({
  expiry,
  className,
}: ExpiringSoonBannerProps) {
  const { t } = useTranslation()

  if (expiry.tier !== "expiring_soon") {
    return null
  }

  // `hours_remaining` is always present alongside `tier === "expiring_soon"`
  // (server contract), but guard for the type union; fall back to 0 so the
  // copy stays sensible rather than rendering "null".
  const hours = expiry.hours_remaining ?? 0

  return (
    <div
      role="status"
      data-testid="mission-detail-expiring-soon"
      className={cn(
        "flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700/50 dark:bg-amber-950/30",
        className,
      )}
    >
      <Badge
        className="w-fit gap-1 bg-amber-200 text-amber-900 dark:bg-amber-800/40 dark:text-amber-100"
        data-testid="mission-detail-expiring-soon-pill"
      >
        <AlarmClock className="size-3.5" aria-hidden="true" />
        {t("creator_mission_detail.expiring_soon.pill", { count: hours })}
      </Badge>
      <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
        {t("creator_mission_detail.expiring_soon.headline")}
      </p>
      <p className="text-sm text-amber-800 dark:text-amber-200/90">
        {t("creator_mission_detail.expiring_soon.body", { count: hours })}
      </p>
    </div>
  )
}
